<template>
    <InsightCardLayout class="relative">
        <Transition mode="out-in" name="fade">
            <div :key="category" class="mb-3 flex items-center gap-1.5">
                <InsightCategoryLabel :label="category" :tier="tier" />
                <span v-if="dateLabel" class="scaffold-details-small text-[#595959]">• {{ dateLabel }}</span>
                <!-- In-flow (no z-index) with negative margins so revealing it never shifts the row. -->
                <button
                    v-if="showClose"
                    type="button"
                    class="-my-1.5 -mr-1.5 ml-auto flex size-7 shrink-0 items-center justify-center rounded-full text-valence-grey-400 transition-colors hover:bg-valence-grey-50 hover:text-valence-grey-600 focus-ring"
                    aria-label="Dismiss insights"
                    @click="$emit('close')">
                    <i class="bi bi-x-lg text-[13px]" aria-hidden="true"></i>
                </button>
            </div>
        </Transition>

        <!-- min-heights keep short cards aligned while letting longer title/summary expand the card to fit. -->
        <Transition mode="out-in" name="fade">
            <div :key="title" class="mb-3 flex min-h-14 items-center">
                <h3 class="display-small-serif text-valence-grey-850">
                    <MarkdownText :text="title" inline />
                </h3>
            </div>
        </Transition>

        <Transition mode="out-in" name="fade">
            <div :key="summary">
                <MarkdownText
                    :text="summary"
                    class="min-h-15 body-small prose prose-sm prose-slate max-w-none text-[#595959] prose-headings:my-0.5 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-li:leading-5" />
            </div>
        </Transition>
        <div class="mt-4 border-t border-valence-grey-250" role="separator"></div>

        <Transition mode="out-in" name="fade">
            <div :key="sources.join('')" class="mt-3 flex items-center justify-between gap-3">
                <InsightEvidenceChips
                    :evidence-groups="evidenceGroups"
                    :legacy-sources="sources"
                    :analytics-context="{ insightId, experimentSlug, location: 'mywork_homepage_carousel' }" />

                <DiveDeeperButton class="animate-shadow-pulse" :loading="isStartingChat" @click="$emit('diveDeeper')" />
            </div>
        </Transition>
        <span v-if="chatError" role="alert" class="details mt-1 text-red-600">{{ chatError }}</span>
    </InsightCardLayout>
</template>

<script setup lang="ts">
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import DiveDeeperButton from "~vue/Labs/DiveDeeperButton.vue";
import { evidenceSourceCounts, type InsightEvidenceGroup } from "~vue/Labs/evidence";
import { getInsightSourcesUsed, tierForExperimentSlug } from "~vue/Labs/insightCategory";
import InsightCategoryLabel from "~vue/Labs/InsightCategoryLabel.vue";
import InsightEvidenceChips from "~vue/Labs/InsightEvidenceChips.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { DateTime } from "luxon";
import { computed, onMounted } from "vue";

const props = withDefaults(
    defineProps<{
        insightId: string;
        category: string;
        experimentSlug: string;
        title: string;
        summary: string;
        createdAt?: string | null;
        showClose?: boolean;
        perspectiveEnabled?: boolean;
        collaborationProfileEnabled?: boolean;
        workIqEnabled?: boolean;
        perspectivePresent?: boolean;
        collaborationProfilePresent?: boolean;
        workIqPresent?: boolean;
        calendarEnabled?: boolean;
        evidenceGroups?: InsightEvidenceGroup[];
        isStartingChat?: boolean;
        chatError?: string | null;
    }>(),
    {
        createdAt: null,
        showClose: false,
        perspectiveEnabled: false,
        collaborationProfileEnabled: false,
        workIqEnabled: false,
        perspectivePresent: false,
        collaborationProfilePresent: false,
        workIqPresent: false,
        calendarEnabled: true,
        isStartingChat: false,
        chatError: null,
    },
);

defineEmits<{ close: []; diveDeeper: [] }>();

const tier = computed(() => tierForExperimentSlug(props.experimentSlug));

const sources = computed(() => getInsightSourcesUsed(tier.value, props));

const dateLabel = computed(() => {
    if (!props.createdAt) {
        return "";
    }
    const created = DateTime.fromISO(props.createdAt);
    return created.isValid ? created.toFormat("MMM d") : "";
});

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_CARD_SHOWN, {
        insight_id: props.insightId,
        experiment_slug: props.experimentSlug,
        location: "mywork_homepage_carousel",
        evidence_source_counts: evidenceSourceCounts(props.evidenceGroups ?? []),
    });
});
</script>
